import type { Store } from 'redux';
import type { ReduxState } from './types.ts';

interface Base {
  connectedCallback(): void;
  disconnectedCallback(): void;
}

interface Connected {
  connectedCallback(): void;
  disconnectedCallback(): void;
  stateChanged(currentState: ReduxState, previousState: ReduxState): void;
}

/**
 * Connects the given element to the store.
 * @example
 * class MyElement extends connect(getStore)(LitElement) {
 *   stateChanged(currentState: ReduxState, previousState: ReduxState) {
 *     // state changed
 *   }
 * }
 */
export function connect(storeOrStoreGetter: Store | (() => Store)) {
  return function <T extends Base>(SuperClass: AbstractConstructor<T>) {
    const getStore = typeof storeOrStoreGetter === 'function'
      ? storeOrStoreGetter
      : () => storeOrStoreGetter;

    let lastState: ReduxState;
    let unsubscribe: (() => void) | undefined;

    function handleChange(this: ConnectedClass) {
      const state = getStore().getState();

      this.stateChanged(state, lastState);

      lastState = state;
    }

    class ConnectedClass extends (SuperClass as AbstractConstructor<Base>) {
      connectedCallback() {
        const store = getStore();

        lastState = store.getState();
        unsubscribe = store.subscribe(handleChange.bind(this));

        this.stateChanged(store.getState(), {});

        super.connectedCallback();
      }

      disconnectedCallback() {
        unsubscribe?.();
        unsubscribe = undefined;

        super.disconnectedCallback();
      }

      stateChanged(_: ReduxState, __: ReduxState) { }
    }

    return ConnectedClass as unknown as AbstractConstructor<Connected & T>;
  }
}